/**
 * useAddBuilding — React-context store for the global "Add building"
 * dialog used across the portal shell.
 *
 * The sidebar's "+ Add building" button used to deep-link into a
 * dedicated /portal/buildings listing page that auto-opened the
 * dialog from a `?add=1` query param. Per Leo 2026-05-06 the listing
 * page is gone and the button now opens the dialog as a modal in
 * place — no route change. This provider is the small piece of glue
 * that lets a sidebar button on any portal page flip the dialog
 * open without prop-drilling through every layout.
 *
 * Mirrors the `useChatSidebar` pattern (same file, smaller surface)
 * so the codebase has one shape for "global UI state in the portal
 * shell." Provider mounts at `PortalLayout`; the dialog itself is
 * rendered there too, alongside `<AskClearFab>`.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import type { AddBuildingPrefill } from "./AddBuildingDialog";

interface AddBuildingContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  /**
   * Optional Step-2 prefill payload. When non-null, the dialog opens
   * directly on the "Confirm building details" step scoped to the
   * given BIN (synthetic TrackingResult; see AddBuildingDialog.tsx).
   *
   * Phase 3.A entry point: dashboard "Confirm details to estimate"
   * CTA pushes the row's bin/bbl/grossSqft here, then flips
   * ``open=true`` — so the user lands on Step 2 instead of the
   * address/BIN/CSV lookup tabs.
   */
  prefill: AddBuildingPrefill | null;
  setPrefill: (prefill: AddBuildingPrefill | null) => void;
  /**
   * Convenience: set ``prefill`` + ``open=true`` in one call, the
   * common case for "open the dialog scoped to this building". Pass
   * ``null`` (or no argument) to open in lookup mode without a prefill.
   */
  openWithPrefill: (prefill?: AddBuildingPrefill | null) => void;
}

const AddBuildingContext = createContext<AddBuildingContextValue | undefined>(
  undefined,
);

export const AddBuildingProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [open, setOpenState] = useState(false);
  const [prefill, setPrefillState] = useState<AddBuildingPrefill | null>(null);

  // Clear the prefill on close so the next openWithPrefill(null) /
  // sidebar "Add building" click lands on Step 1 (lookup), not on a
  // stale Step-2 scoped to whatever row the user previously clicked.
  const setOpen = useCallback((next: boolean) => {
    setOpenState(next);
    if (!next) {
      setPrefillState(null);
    }
  }, []);

  const setPrefill = useCallback(
    (next: AddBuildingPrefill | null) => {
      setPrefillState(next);
    },
    [],
  );

  const openWithPrefill = useCallback(
    (next?: AddBuildingPrefill | null) => {
      setPrefillState(next ?? null);
      setOpenState(true);
    },
    [],
  );

  const value = useMemo<AddBuildingContextValue>(
    () => ({ open, setOpen, prefill, setPrefill, openWithPrefill }),
    [open, setOpen, prefill, setPrefill, openWithPrefill],
  );
  return (
    <AddBuildingContext.Provider value={value}>
      {children}
    </AddBuildingContext.Provider>
  );
};

export const useAddBuilding = (): AddBuildingContextValue => {
  const ctx = useContext(AddBuildingContext);
  if (!ctx) {
    throw new Error(
      "useAddBuilding must be used inside <AddBuildingProvider>. " +
        "It is mounted at the portal shell (PortalLayout).",
    );
  }
  return ctx;
};
